import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    draftProducts: 0,
    mediaCount: 0,
    todayUploads: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [products, media] = await Promise.all([
          api.products.list({ limit: 100 }),
          api.media.list({ limit: 100 }),
        ]);

        const today = new Date().toISOString().split('T')[0];
        const todayCount = products.products.filter(
          (p) => p.createdAt?.startsWith(today),
        ).length;

        setStats({
          totalProducts: products.total,
          activeProducts: products.products.filter((p) => p.status === 'active').length,
          draftProducts: products.products.filter((p) => p.status === 'draft').length,
          mediaCount: media.total,
          todayUploads: todayCount,
        });
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    {
      label: 'Total Products',
      value: stats.totalProducts,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      color: 'bg-primary-100 text-primary-600',
    },
    {
      label: 'Active Products',
      value: stats.activeProducts,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'bg-success/10 text-success',
    },
    {
      label: 'Today Uploads',
      value: stats.todayUploads,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3" />
        </svg>
      ),
      color: 'bg-warning/10 text-warning',
    },
    {
      label: 'Media Files',
      value: stats.mediaCount,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: 'bg-secondary-100 text-secondary-600',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Dashboard</h2>
        <p className="text-muted-foreground">Overview of your product catalog</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <p className="mt-1 text-2xl font-bold text-foreground">
                  {loading ? '-' : stat.value}
                </p>
              </div>
              <div className={`rounded-lg p-2 ${stat.color}`}>{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Quick Actions</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Link to="/upload-new" className="flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent">
              <div className="rounded-lg bg-primary-100 p-2 text-primary-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Upload New Product</p>
                <p className="text-2xs text-muted-foreground">Add a single product</p>
              </div>
            </Link>
            <Link to="/batch-import" className="flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent">
              <div className="rounded-lg bg-success/10 p-2 text-success">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Batch Import</p>
                <p className="text-2xs text-muted-foreground">Import via CSV</p>
              </div>
            </Link>
            <Link to="/media-library" className="flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent">
              <div className="rounded-lg bg-secondary-100 p-2 text-secondary-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Media Library</p>
                <p className="text-2xs text-muted-foreground">Manage images & videos</p>
              </div>
            </Link>
            <Link to="/settings" className="flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent">
              <div className="rounded-lg bg-muted p-2 text-muted-foreground">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Settings</p>
                <p className="text-2xs text-muted-foreground">Configure uploader</p>
              </div>
            </Link>
          </div>
        </div>

        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Draft Products</h3>
          {stats.draftProducts > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                You have {stats.draftProducts} draft product{stats.draftProducts > 1 ? 's' : ''} waiting to be published.
              </p>
              <Link to="/upload-new" className="btn-primary">
                Continue Editing
              </Link>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-6 text-center">
              <p className="text-sm text-muted-foreground">No draft products</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
